import React, { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { canViewRefunds } from '../global_context/capabilities/refundCapabilities'
import {
  canViewPosAnalysisDownload,
  canViewPosEffectiveness,
  canViewPosTasks
} from '../global_context/capabilities/posCapabilities'
import {
  canViewRbacRoles,
  canViewRbacUsers
} from '../global_context/capabilities/rbacManagementCapabilities'
import {
  canViewClientApplications,
  canViewClients
} from '../global_context/capabilities/clientCapabilities'
import { canViewProducts } from '../global_context/capabilities/productCapabilities'
import { canViewSegments } from '../global_context/capabilities/segmentCapabilities'
import { canViewEssentials } from '../global_context/capabilities/essentialCapabilities'
import {
  canViewPriceLists,
  canViewSpecialPrices
} from '../global_context/capabilities/priceCapabilities'
import { canViewPromotions } from '../global_context/capabilities/promoCapabilities'
import { canViewUsersApp } from '../global_context/capabilities/userAppCapabilities'
import { canViewRoutePlans } from '../global_context/capabilities/routePlanCapabilities'
import {
  canViewPocFillRate,
  canViewPocOrders,
  canViewPocStoreRelations
} from '../global_context/capabilities/pocCapabilities'
import {
  canViewAssignDeliveries,
  canViewDeliveries
} from '../global_context/capabilities/deliveryCapabilities'
import { canViewQuotes } from '../global_context/capabilities/quoteCapabilities'
import { canViewOrders } from '../global_context/capabilities/orderCapabilities'
import { canViewOnBoardSales } from '../global_context/capabilities/onBoardSaleCapabilities'
import { canViewCharges } from '../global_context/capabilities/chargeCapabilities'
import {
  canViewAssetReports,
  canViewCloseAssets
} from '../global_context/capabilities/assetCapabilities'
import { canViewSettlements } from '../global_context/capabilities/settlementCapabilities'
import { canViewReports } from '../global_context/capabilities/reportCapabilities'
import { NavIcon } from './navIcons'
import NavItem from './NavItem'
import { useAuth } from '../auth/AuthContext'

const NavBar = (props) => {
  // Refresh cookie-based permissions when session restoration completes.
  useAuth()
  const { collapsed, embeddedInDrawer } = props
  const navigate = useNavigate()
  const canSeeRbacRoles = canViewRbacRoles()
  const canSeeRbacUsers = canViewRbacUsers()
  const canSeeConfigUsers = canViewUsersApp()
  const canSeeClients = canViewClients()
  const canSeeClientApplications = canViewClientApplications()
  const canSeeClientsGroup = canSeeClients || canSeeClientApplications
  const canSeePricesGroup = canViewPriceLists() || canViewSpecialPrices()
  const canSeeRbacGroup = canSeeRbacUsers || canSeeRbacRoles
  const canSeePocStoreRelations = canViewPocStoreRelations()
  const canSeeAssetsGroup = canViewAssetReports() || canViewCloseAssets()

  const canSeePlanifica =
    canSeeClientsGroup ||
    canViewProducts() ||
    canViewSegments() ||
    canViewEssentials() ||
    canSeePricesGroup ||
    canViewPromotions() ||
    canViewPosTasks() ||
    canSeeConfigUsers ||
    canSeeRbacGroup ||
    canViewRoutePlans() ||
    canSeePocStoreRelations ||
    canViewAssignDeliveries()

  const canSeeEjecuta =
    canViewPocOrders() ||
    canViewQuotes() ||
    canViewOrders() ||
    canViewRefunds() ||
    canViewDeliveries() ||
    canViewOnBoardSales() ||
    canViewCharges() ||
    canViewPosEffectiveness() ||
    canSeeAssetsGroup ||
    canViewSettlements()

  const canSeeMide = canViewPocFillRate() || canViewReports() || canViewPosAnalysisDownload()

  const handleClick = (event) => {
    if (props.isUpdating) {
      event.preventDefault()
      alert(
        'Estamos procesando tu solicitud. Por favor, espera unos segundos... No cierres o recargues la página.'
      )
      return
    }

    windowConfirmEvent(event)
  }

  const windowConfirmEvent = (event) => {
    if (event.target.tagName === 'A') {
      let href = event.target.getAttribute('href')
      if (props.isEditing === true) {
        const val = window.confirm(
          'Esta seguro que quiere continuar? Los cambios editados no seran guardados'
        )
        if (val === true && href) {
          navigate(href)
        }
      } else if (href) {
        navigate(href)
      }
    }
  }

  return (
    <div
      className={`nav-bar ${collapsed ? 'collapsed' : ''} ${embeddedInDrawer ? 'nav-bar--drawer' : ''}`}
      onClick={handleClick}
    >
      <div
        className="logo"
        role="link"
        tabIndex={0}
        onClick={() => navigate('/inicio')}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            navigate('/inicio')
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <img src="/assets/ubiqua_logo.svg" className={'ubiqua-logo'} alt="Ubiqua logo" />
        <img
          src="/assets/ubiqua_name.svg"
          className={`ubiqua-name ${collapsed ? 'collapsed' : ''}`}
          alt="Ubiqua name"
        />
      </div>

      <div className="nav-items-container">
        <NavItem
          collapsed={collapsed}
          itemName={'Inicio'}
          url={'/inicio'}
          exact
          canBeSeen={true}
          level={0}
          icon={<NavIcon name="home" />}
        />

        <NavItem
          collapsed={collapsed}
          itemName={'Planifica'}
          canBeSeen={canSeePlanifica}
          level={0}
          icon={<NavIcon name="event_note" />}
        >
          <NavItem
            collapsed={collapsed}
            itemName={'Clientes'}
            url={null}
            canBeSeen={canSeeClientsGroup}
            level={1}
            icon={<NavIcon name="store" />}
          >
            <NavItem
              collapsed={collapsed}
              itemName={'Listado de clientes'}
              url={'/clientes'}
              canBeSeen={canSeeClients}
              level={2}
              activeURLs={['/clientes', '/crear_cliente']}
            />
            <NavItem
              collapsed={collapsed}
              itemName={'Solicitud cliente nuevo'}
              url={'/solicitud_cliente_nuevo'}
              canBeSeen={canSeeClientApplications}
              level={2}
              activeURLs={['/solicitud_cliente_nuevo', '/crear_solicitud_cliente_nuevo']}
            />
          </NavItem>
          <NavItem
            collapsed={collapsed}
            itemName={'Productos'}
            url={'/productos'}
            canBeSeen={canViewProducts()}
            level={1}
            icon={<NavIcon name="work" />}
            activeURLs={['/productos', '/crear_producto']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Segmentos'}
            url={'/segmentos'}
            canBeSeen={canViewSegments()}
            level={1}
            icon={<NavIcon name="hub" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Infaltables'}
            url={'/infaltables'}
            canBeSeen={canViewEssentials()}
            level={1}
            icon={<NavIcon name="shopping_basket" />}
            activeURLs={['/infaltables', '/asociar_infaltable']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Precios'}
            url={null}
            canBeSeen={canSeePricesGroup}
            level={1}
            icon={<NavIcon name="payments" />}
          >
            <NavItem
              collapsed={collapsed}
              itemName={'Listas de Precios'}
              url={'/listas_de_precios'}
              canBeSeen={canViewPriceLists()}
              level={2}
              activeURLs={['/listas_de_precios', '/crear_lista_de_precios']}
            />
            <NavItem
              collapsed={collapsed}
              itemName={'Precios Especiales'}
              url={'/precios_especiales'}
              canBeSeen={canViewSpecialPrices()}
              level={2}
              activeURLs={['/precios_especiales', '/crear_precio_especial']}
            />
          </NavItem>
          <NavItem
            collapsed={collapsed}
            itemName={'Promociones'}
            url={'/promociones'}
            canBeSeen={canViewPromotions()}
            level={1}
            icon={<NavIcon name="redeem" />}
            activeURLs={['/promociones', '/nueva_promo']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Tareas en PDV'}
            url={'/ejecucion_punto_venta'}
            canBeSeen={canViewPosTasks()}
            level={1}
            icon={<NavIcon name="checklist" />}
            activeURLs={['/ejecucion_punto_venta', '/crear_tarea_punto_venta']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Usuarios App'}
            url={'/usuarios'}
            canBeSeen={canSeeConfigUsers}
            level={1}
            icon={<NavIcon name="group" />}
            activeURLs={['/usuarios', '/crear_usuario_configuracion']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Usuarios Torre'}
            url={null}
            canBeSeen={canSeeRbacGroup}
            level={1}
            icon={<NavIcon name="admin_panel_settings" />}
            activeURLs={[
              '/configuracion/rbac/asignar',
              '/configuracion/rbac/roles',
              '/configuracion/rbac/roles/nuevo'
            ]}
          >
            <NavItem
              collapsed={collapsed}
              itemName={'Listado de usuarios'}
              url={'/configuracion/rbac/asignar'}
              canBeSeen={canSeeRbacUsers}
              level={2}
              activeURLs={['/configuracion/rbac/asignar']}
            />
            <NavItem
              collapsed={collapsed}
              itemName={'Roles y permisos'}
              url={'/configuracion/rbac/roles'}
              canBeSeen={canSeeRbacRoles}
              level={2}
              activeURLs={['/configuracion/rbac/roles', '/configuracion/rbac/roles/nuevo']}
            />
          </NavItem>
          <NavItem
            collapsed={collapsed}
            itemName={'Plan de visitas'}
            url={'/plan_de_visitas'}
            canBeSeen={canViewRoutePlans()}
            level={1}
            icon={<NavIcon name="route" />}
            activeURLs={['/plan_de_visitas', '/crear_plan_de_visita']}
          />

          <NavItem
            collapsed={collapsed}
            itemName={'POC'}
            url={null}
            canBeSeen={canSeePocStoreRelations}
            level={1}
            icon={<NavIcon name="account_tree" />}
          >
            <NavItem
              collapsed={collapsed}
              itemName={'Asignar relaciones'}
              url={'/store_relations'}
              canBeSeen={canSeePocStoreRelations}
              level={2}
              activeURLs={['/store_relations']}
            />
          </NavItem>

          <NavItem
            collapsed={collapsed}
            itemName={'Asignación de entregas'}
            url={'/asignar_entregas'}
            canBeSeen={canViewAssignDeliveries()}
            level={1}
            icon={<NavIcon name="assignment_ind" />}
          />
        </NavItem>
        <NavItem
          collapsed={collapsed}
          itemName={'Ejecuta'}
          canBeSeen={canSeeEjecuta}
          level={0}
          icon={<NavIcon name="person" />}
        >
          <NavItem
            collapsed={collapsed}
            itemName={'Órdenes de compra'}
            url={'/ordenes_oc'}
            canBeSeen={canViewPocOrders()}
            level={1}
            activeURLs={['/ordenes_oc', '/ordenes_oc_details']}
            icon={<NavIcon name="assignment" />}
          />

          <NavItem
            collapsed={collapsed}
            itemName={'Cotizaciones'}
            url={'/cotizaciones'}
            canBeSeen={canViewQuotes()}
            level={1}
            icon={<NavIcon name="tag" />}
          />

          <NavItem
            collapsed={collapsed}
            itemName={'Pedidos'}
            url={'/pedidos'}
            canBeSeen={canViewOrders()}
            level={1}
            icon={<NavIcon name="shopping_cart" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Devoluciones'}
            url={'/devoluciones'}
            canBeSeen={canViewRefunds()}
            level={1}
            icon={<NavIcon name="undo" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Entregas'}
            url={'/entregas'}
            canBeSeen={canViewDeliveries()}
            level={1}
            icon={<NavIcon name="local_shipping" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Venta a bordo'}
            url={'/ventas_a_bordo'}
            canBeSeen={canViewOnBoardSales()}
            level={1}
            icon={<NavIcon name="electric_moped" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Cobros'}
            url={'/cobros'}
            canBeSeen={canViewCharges()}
            level={1}
            icon={<NavIcon name="paid" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Ejecuciones en PDV'}
            url={'/efectividad_de_tareas'}
            canBeSeen={canViewPosEffectiveness()}
            level={1}
            icon={<NavIcon name="analytics" />}
            activeURLs={['/efectividad_de_tareas', '/tareas_en_sitio']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Activos'}
            url={null}
            canBeSeen={canSeeAssetsGroup}
            level={1}
            icon={<NavIcon name="inventory_2" />}
          >
            <NavItem
              collapsed={collapsed}
              itemName={'Reporte de activos'}
              url={'/reporte_activos'}
              canBeSeen={canViewAssetReports()}
              level={2}
            />
            <NavItem
              collapsed={collapsed}
              itemName={'Cierre de activos'}
              url={'/cierre_activos'}
              canBeSeen={canViewCloseAssets()}
              level={2}
            />
          </NavItem>
          <NavItem
            collapsed={collapsed}
            itemName={'Liquidaciones'}
            url={'/liquidaciones'}
            canBeSeen={canViewSettlements()}
            level={1}
            icon={<NavIcon name="currency_exchange" />}
          />
        </NavItem>
        <NavItem
          collapsed={collapsed}
          itemName={'Mide'}
          canBeSeen={canSeeMide}
          level={0}
          icon={<NavIcon name="monitoring" />}
        >
          <NavItem
            collapsed={collapsed}
            itemName={'Fill Rate por cadena'}
            url={'/metricas_gerencia'}
            canBeSeen={canViewPocFillRate()}
            level={1}
            icon={<NavIcon name="finance_mode" />}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Reportes'}
            url={'/reportes'}
            canBeSeen={canViewReports()}
            level={1}
            icon={<NavIcon name="bar_chart" />}
            activeURLs={['/reportes', '/usuario_reportes', '/visitas-no-efectivas']}
          />
          <NavItem
            collapsed={collapsed}
            itemName={'Descarga de reportes'}
            url={'/descarga_analisis_pdv'}
            canBeSeen={canViewPosAnalysisDownload()}
            level={1}
            icon={<NavIcon name="download_2" />}
          />
        </NavItem>
      </div>
    </div>
  )
}

export default memo(NavBar)
